import React, { useEffect, useState } from 'react';
import FitText from '../../FitText';
import { BannerPreviewContent, PreviewProps } from '../Style';
import Viewer from '../../../../Viewer';
import ScrollableContent from '../../../../ScrollableContent';
import { calculateZoom } from '../../../../../store/form/utils';
import BannerContentPreviewFrame from './Frame';
import FitPrice from '../../FitPrice';
import FitEconomize from '../../FitEconomize';
import Canvas from '../../../../Canvas';
import {
  capitalizeFirstLetter,
  removeDuplicates,
} from '../../../../../store/utils';

export const calculeInstallmentPrice = (
  price: number,
  installments: number,
) => {
  return installments ? Math.round(price / installments) : price;
};
interface BannerContentPreview
  extends React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  > {
  width: number;
  height: number;
  zoom?: number;
  backgroundImageUrl: string;
  elements: any;
}

function BannerContentPreview({
  width,
  height,
  zoom,
  backgroundImageUrl,
  elements,
  ...props
}: BannerContentPreview) {
  const [transition, setTransition] = useState(0);
  useEffect(() => {
    if (!zoom) return;
    if (Math.abs(zoom - 0.5) < 0.06) {
      setTransition(300);
    } else {
      setTransition(0);
    }
  }, [zoom]);

  const previewProps: PreviewProps = {
    $width: width,
    $height: height,
    $zoom: zoom,
    $backgroundImage: backgroundImageUrl,
  };

  const [fontsLoaded, setFontsLoaded] = useState(false);
  React.useEffect(() => {
    let fontData: { fontFamily: string; fontWeight: string }[] = [];
    fontData = [
      ...fontData,
      ...elements.map(({ fontFamily, fontWeight }) => ({
        fontFamily,
        fontWeight,
      })),
    ];
    Promise.all(
      removeDuplicates(fontData).map(({ fontFamily, fontWeight }) => {
        const font = new FontFace(
          `${fontFamily}-${fontWeight}`,
          `url(/${capitalizeFirstLetter(fontFamily)}-${capitalizeFirstLetter(
            fontWeight,
          )}.ttf)`,
        );
        return font.load();
      }),
    )
      .then((fonts) => {
        fonts.forEach((font) => {
          document.fonts.add(font);
        });
        setFontsLoaded(true);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  const ref = React.useRef<HTMLCanvasElement>(null);
  React.useEffect(() => {
    const canvas = ref.current;
    if (canvas && fontsLoaded) {
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        for (const { type, elementType, ...element } of elements) {
          switch (elementType ?? 'text') {
            case 'validity':
              Canvas.Elements.Validity({ element }, ref);
              break;
            default:
              break;
          }
        }
      }
    }
  }, [elements, fontsLoaded]);

  return (
    <ScrollableContent
      width={width}
      height={height}
      scale={Math.max(calculateZoom(0.1, 3, zoom ?? 0.5), 1)}
    >
      <BannerPreviewContent
        key={1}
        style={{
          transition: `transform ${transition}ms, min-height 500ms, max-height 500ms`,
        }}
        {...previewProps}
        {...props}
      >
        <Canvas
          ref={ref}
          elements={elements}
          style={{
            position: 'absolute',
            left: 0,
          }}
        />
        {elements.map(({ type, elementType, ...element }) => {
          switch (elementType ?? 'text') {
            case 'text':
              return <FitText key={type} {...element} />;
            case 'economize':
              return <FitEconomize key={type} {...element} />;
            case 'price':
              return <FitPrice key={type} {...element} />;
            case 'qrcode':
              return <Viewer.QRCode key={type} {...element} />;
            case 'barcode':
              return <Viewer.Barcode key={type} {...element} />;
            default:
              return null;
          }
        })}
      </BannerPreviewContent>
    </ScrollableContent>
  );
}

export default Object.assign(BannerContentPreview, {
  Frame: BannerContentPreviewFrame,
});
